import type {
  SchoolStudentCountOperator,
  SchoolStudentExportColumn,
  SchoolStudentListFilters,
  SchoolStudentTextOperator,
  SchoolStudentTuitionStatus,
} from "~/types/schoolManagement";
import type { GridFilterModel, GridSortModel } from "~/utils/agGridTable";

const textOperators = {
  contains: "contains",
  notContains: "not_contains",
  equals: "equals",
  notEqual: "not_equals",
  startsWith: "starts_with",
  endsWith: "ends_with",
  blank: "is_empty",
  notBlank: "is_not_empty",
} as const;
const countOperators = {
  equals: "eq",
  notEqual: "ne",
  lessThan: "lt",
  lessThanOrEqual: "lte",
  greaterThan: "gt",
  greaterThanOrEqual: "gte",
  inRange: "between",
} as const;
const textColumns = [
  { column: "name", field: "name", label: "Aluno", limit: 255 },
  { column: "email", field: "email", label: "Email", limit: 255 },
  { column: "teachers", field: "teacher", label: "Professores", limit: 511 },
] as const;
const sortableColumns: Record<string, string> = {
  name: "name",
  email: "email",
  teachers: "teacher",
  classrooms: "classroom_count",
  status: "status",
  tuition: "tuition",
};
const exportColumns = new Map<string, SchoolStudentExportColumn>([
  ["name", "name"],
  ["email", "email"],
  ["teachers", "teachers"],
  ["classrooms", "classroom_count"],
  ["status", "status"],
  ["tuition", "tuition"],
]);
const tuitionStatuses = ["not_configured", "enabled", "disabled"] as const;

/** Keep the displayed order while excluding action and selection columns. */
export function getSchoolStudentExportColumns(
  columnIds: readonly string[],
): SchoolStudentExportColumn[] {
  return [
    ...new Set(
      columnIds.flatMap((id) => {
        const field = exportColumns.get(id);
        return field ? [field] : [];
      }),
    ),
  ];
}

const single = (value: unknown) =>
  String(Array.isArray(value) ? (value[0] ?? "") : (value ?? ""));
const positive = (value: unknown) => {
  const number = Number(single(value));
  return Number.isSafeInteger(number) && number > 0 ? number : null;
};
const optionalBoolean = (value: unknown) => {
  const text = single(value);
  return text === "true" ? true : text === "false" ? false : undefined;
};
const isEmptyOperator = (operator: string | undefined) =>
  operator === "is_empty" || operator === "is_not_empty";
const countOperand = (value: unknown) => {
  const text = single(value).trim();
  return /^\d+$/.test(text) && Number(text) <= 2147483647 ? Number(text) : text;
};
const record = (value: unknown): Record<string, unknown> =>
  value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};

/** Separate from the classroom parser: students have their own REST contract. */
export function parseSchoolStudentListQuery(
  query: Record<string, unknown>,
): SchoolStudentListFilters {
  const status = single(query.status);
  const size = Number(single(query.page_size));
  const result: SchoolStudentListFilters = {
    search: single(query.search).trim(),
    status: status === "active" || status === "archived" ? status : "all",
    teacher_id: positive(query.teacher_id),
    classroom_id: positive(query.classroom_id),
    unassigned: optionalBoolean(query.unassigned),
    include_archived: optionalBoolean(query.include_archived),
    page: positive(query.page) || 1,
    page_size: [10, 25, 50, 100].includes(size) ? size : 25,
  };
  const ordering = single(query.ordering)
    .split(",")
    .map((field) => field.trim())
    .join(",");
  if (ordering) result.ordering = ordering;
  if (query.tuition_status !== undefined)
    result.tuition_status = single(query.tuition_status) as
      | SchoolStudentTuitionStatus
      | "all";
  for (const { field } of textColumns) {
    const operator = single(query[`${field}_op`]);
    // Keep unknown raw operators until validation rather than widening a link.
    if (query[`${field}_op`] !== undefined)
      result[`${field}_op`] = operator as SchoolStudentTextOperator;
    if (query[`${field}_value`] !== undefined)
      result[`${field}_value`] = single(query[`${field}_value`]);
  }
  const countOperator = single(query.classroom_count_op);
  if (query.classroom_count_op !== undefined)
    result.classroom_count_op = countOperator as SchoolStudentCountOperator;
  if (query.classroom_count_value !== undefined)
    result.classroom_count_value = countOperand(query.classroom_count_value);
  if (query.classroom_count_to !== undefined)
    result.classroom_count_to = countOperand(query.classroom_count_to);
  return result;
}

export function validateSchoolStudentListQuery(
  filters: SchoolStudentListFilters,
) {
  let searchError = "";
  let filterError = "";
  if ([...filters.search].length > 255)
    searchError = "Use até 255 caracteres na busca.";
  else if (
    new Set(filters.search.trim().toLowerCase().split(/\s+/).filter(Boolean))
      .size > 10
  )
    searchError = "Use até 10 palavras diferentes na busca.";
  if (
    filters.tuition_status !== undefined &&
    filters.tuition_status !== "all" &&
    !tuitionStatuses.includes(filters.tuition_status)
  )
    filterError = "Escolha uma situação válida para a mensalidade.";
  for (const { field, label, limit } of textColumns) {
    const operator = filters[`${field}_op`];
    const value = filters[`${field}_value`];
    if (
      operator !== undefined &&
      !Object.values(textOperators).includes(operator)
    )
      filterError ||= `Escolha uma operação válida para ${label}.`;
    if (!operator && value !== undefined)
      filterError ||= `Escolha uma operação válida para ${label}.`;
    if (operator && !isEmptyOperator(operator) && !value?.trim())
      filterError ||= `Informe um texto para filtrar ${label}.`;
    if (operator && isEmptyOperator(operator) && value !== undefined)
      filterError ||= `O filtro vazio de ${label} não recebe um texto.`;
    if (value && [...value].length > limit)
      filterError ||= `Use até ${limit} caracteres no filtro ${label}.`;
  }
  const operator = filters.classroom_count_op;
  const value = filters.classroom_count_value;
  const to = filters.classroom_count_to;
  if (
    operator !== undefined &&
    !Object.values(countOperators).includes(operator)
  )
    filterError ||= "Escolha uma operação válida para a quantidade de turmas.";
  if (!operator && (value !== undefined || to !== undefined))
    filterError ||= "Escolha uma operação válida para a quantidade de turmas.";
  if (
    operator &&
    (typeof value !== "number" ||
      !Number.isInteger(value) ||
      value < 0 ||
      value > 2147483647)
  )
    filterError ||=
      "Informe uma quantidade inteira de turmas entre 0 e 2147483647.";
  if (
    operator === "between" &&
    (typeof to !== "number" ||
      !Number.isInteger(to) ||
      to < 0 ||
      to > 2147483647)
  )
    filterError ||= "Informe os dois limites inteiros do intervalo de turmas.";
  if (
    operator === "between" &&
    typeof value === "number" &&
    typeof to === "number" &&
    value > to
  )
    filterError ||=
      "O início do intervalo de turmas deve ser menor ou igual ao fim.";
  if (operator && operator !== "between" && to !== undefined)
    filterError ||=
      "O limite final de turmas só pode ser usado em um intervalo.";
  if (filters.ordering) {
    const ordering = filters.ordering.split(",");
    const fields = ordering.map((field) => field.replace(/^-/, ""));
    if (
      ordering.length > 5 ||
      new Set(fields).size !== fields.length ||
      fields.some(
        (field) =>
          ![...Object.values(sortableColumns), "created_at"].includes(field),
      )
    )
      filterError ||= "Use até cinco colunas diferentes na ordenação.";
  }
  return { searchError, filterError };
}

/** Include only named domain fields, never a serialized grid model. */
export function buildSchoolStudentListQuery(
  filters: SchoolStudentListFilters,
): Record<string, string> {
  const result: Record<string, string> = {
    status: filters.status,
    page: String(filters.page),
    page_size: String(filters.page_size),
  };
  if (filters.search.trim()) result.search = filters.search.trim();
  if (filters.ordering) result.ordering = filters.ordering;
  if (filters.tuition_status && filters.tuition_status !== "all")
    result.tuition_status = filters.tuition_status;
  if (filters.teacher_id) result.teacher_id = String(filters.teacher_id);
  if (filters.classroom_id) result.classroom_id = String(filters.classroom_id);
  if (filters.unassigned !== undefined)
    result.unassigned = String(filters.unassigned);
  if (filters.include_archived !== undefined)
    result.include_archived = String(filters.include_archived);
  for (const { field } of textColumns) {
    const operator = filters[`${field}_op`];
    if (!operator) continue;
    result[`${field}_op`] = operator;
    if (!isEmptyOperator(operator))
      result[`${field}_value`] = filters[`${field}_value`]?.trim() || "";
  }
  if (filters.classroom_count_op) {
    result.classroom_count_op = filters.classroom_count_op;
    result.classroom_count_value = String(filters.classroom_count_value ?? "");
    if (filters.classroom_count_op === "between")
      result.classroom_count_to = String(filters.classroom_count_to ?? "");
  }
  return result;
}

export function schoolStudentGridFilterModel(
  filters: SchoolStudentListFilters,
): GridFilterModel {
  const result: GridFilterModel = {};
  for (const { field, column } of textColumns) {
    const operator = filters[`${field}_op`];
    if (!operator) continue;
    const type = Object.entries(textOperators).find(
      ([, value]) => value === operator,
    )?.[0];
    if (!type) continue;
    result[column] = {
      filterType: "text",
      type,
      ...(isEmptyOperator(operator)
        ? {}
        : { filter: filters[`${field}_value`] ?? "" }),
    };
  }
  if (filters.classroom_count_op) {
    const type = Object.entries(countOperators).find(
      ([, value]) => value === filters.classroom_count_op,
    )?.[0];
    if (type)
      result.classrooms = {
        filterType: "number",
        type,
        filter: filters.classroom_count_value,
        ...(filters.classroom_count_op === "between"
          ? { filterTo: filters.classroom_count_to }
          : {}),
      };
  }
  if (filters.status !== "all")
    result.status = { filterType: "status", value: filters.status };
  if (filters.tuition_status && filters.tuition_status !== "all")
    result.tuition = { filterType: "tuition", value: filters.tuition_status };
  return result;
}

export function schoolStudentGridSortModel(
  filters: SchoolStudentListFilters,
): GridSortModel {
  return (filters.ordering || "").split(",").flatMap((value) => {
    const field = value.replace(/^-/, "");
    const colId = Object.entries(sortableColumns).find(
      ([, domain]) => domain === field,
    )?.[0];
    return colId
      ? [
          {
            colId,
            sort: value.startsWith("-") ? ("desc" as const) : ("asc" as const),
          },
        ]
      : [];
  });
}

export function schoolStudentFiltersFromGrid(
  current: SchoolStudentListFilters,
  model: GridFilterModel,
): SchoolStudentListFilters {
  const result = {
    ...current,
    status: "all" as const,
  } as SchoolStudentListFilters;
  if (
    Object.keys(model).some(
      (column) =>
        model[column] != null &&
        ![
          "name",
          "email",
          "teachers",
          "classrooms",
          "status",
          "tuition",
        ].includes(column),
    )
  )
    throw new Error("Escolha uma coluna válida para filtrar.");
  for (const { field, column } of textColumns) {
    delete result[`${field}_op`];
    delete result[`${field}_value`];
    if (!model[column]) continue;
    const filter = record(model[column]);
    const type = String(filter.type);
    if (
      filter.filterType !== "text" ||
      filter.operator ||
      filter.conditions ||
      !Object.keys(textOperators).includes(type)
    )
      throw new Error("Use somente uma condição de texto por coluna.");
    const operator = textOperators[type as keyof typeof textOperators];
    result[`${field}_op`] = operator;
    if (!isEmptyOperator(operator))
      result[`${field}_value`] = single(filter.filter);
  }
  delete result.classroom_count_op;
  delete result.classroom_count_value;
  delete result.classroom_count_to;
  if (model.classrooms) {
    const filter = record(model.classrooms);
    const type = String(filter.type);
    if (
      filter.filterType !== "number" ||
      filter.operator ||
      filter.conditions ||
      !Object.keys(countOperators).includes(type)
    )
      throw new Error(
        "Use somente uma condição numérica para a quantidade de turmas.",
      );
    result.classroom_count_op =
      countOperators[type as keyof typeof countOperators];
    result.classroom_count_value = countOperand(filter.filter);
    if (result.classroom_count_op === "between")
      result.classroom_count_to = countOperand(filter.filterTo);
  }
  const statusFilter = record(model.status);
  if (model.status && statusFilter.filterType !== "status")
    throw new Error("Escolha um status válido para os alunos.");
  const status = statusFilter.value;
  if (status === "active" || status === "archived") result.status = status;
  else if (model.status && status !== "all")
    throw new Error("Escolha um status válido para os alunos.");
  delete result.tuition_status;
  if (model.tuition) {
    const tuitionFilter = record(model.tuition);
    const tuition = tuitionFilter.value;
    if (
      tuitionFilter.filterType !== "tuition" ||
      (tuition !== "all" &&
        !tuitionStatuses.includes(tuition as SchoolStudentTuitionStatus))
    )
      throw new Error("Escolha uma situação válida para a mensalidade.");
    if (tuition !== "all")
      result.tuition_status = tuition as SchoolStudentTuitionStatus;
  }
  return result;
}

/** An empty model deliberately omits ordering: the backend owns its fallback. */
export function schoolStudentOrderingFromGrid(
  model: GridSortModel,
): string | undefined {
  if (
    model.length > 5 ||
    new Set(model.map(({ colId }) => colId)).size !== model.length
  )
    throw new Error("Use até cinco colunas diferentes na ordenação.");
  return (
    model
      .map(({ colId, sort }) => {
        const field = sortableColumns[colId];
        if (
          !Object.keys(sortableColumns).includes(colId) ||
          (sort !== "asc" && sort !== "desc")
        )
          throw new Error("Escolha uma coluna válida para ordenar.");
        return `${sort === "desc" ? "-" : ""}${field}`;
      })
      .join(",") || undefined
  );
}

/** Operators/status/numbers apply immediately; only text operands debounce. */
export function hasImmediateSchoolStudentFilterChange(
  previous: GridFilterModel,
  next: GridFilterModel,
) {
  if (
    JSON.stringify(previous.status) !== JSON.stringify(next.status) ||
    JSON.stringify(previous.tuition) !== JSON.stringify(next.tuition) ||
    JSON.stringify(previous.classrooms) !== JSON.stringify(next.classrooms)
  )
    return true;
  return textColumns.some(({ column }) => {
    const before = record(previous[column]);
    const after = record(next[column]);
    if (after.type === "blank" || after.type === "notBlank")
      return before.type !== after.type;
    if (before.type === "blank" || before.type === "notBlank")
      return before.type !== after.type;
    // A text filter first appears after its first typed character. That is a
    // value change, while an operator change on an existing filter is immediate.
    return Boolean(before.type && after.type && before.type !== after.type);
  });
}
